/**
 * The Bitcoin panel's data hooks. Thin by design: every calculation lives in
 * `src/lib/bitcoin/**` as a pure function and every request lives in
 * `src/lib/bitcoin/api.ts`. These four hooks only own React state and the
 * polling cadence.
 *
 * All four go through the shared `usePolling`, which pauses while the tab is
 * hidden and backs off exponentially on failure. That is not politeness: a
 * permanently pinned tab is exactly the shape of client that exhausts a free
 * tier overnight, and CoinGecko answers a rate-limited request with a 429 that
 * carries no CORS headers — an unsuppressable console error.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { env, isConfigured } from '../../config/env';
import { usePolling } from '../../lib/poll';
import { usePersistentState } from '../../lib/storage';
import {
  addWallet,
  defaultWallets,
  isWalletList,
  removeWallet,
  updateWallet,
  type Wallet,
} from '../../lib/bitcoin/stack';
import {
  fetchChainTip,
  fetchFearGreed,
  fetchHistory,
  fetchNewhedgePowerLaw,
  fetchSpotPrice,
  readCachedPrice,
  type ChainTip,
  type FearGreed,
  type NewhedgePowerLaw,
  type SpotPrice,
} from '../../lib/bitcoin/api';
import { estimateHalving, type HalvingEstimate } from '../../lib/bitcoin/halving';
import type { PricePoint } from '../../lib/bitcoin/powerLaw';

export type SectionStatus = 'loading' | 'ready' | 'error';

/* ------------------------------------------------------------------ */
/* Spot price                                                          */
/* ------------------------------------------------------------------ */

export interface SpotPriceState {
  readonly price: SpotPrice | null;
  readonly status: SectionStatus;
  /** True when the displayed price is cached and the last refresh failed. */
  readonly stale: boolean;
  readonly error: string | null;
  readonly refresh: () => void;
}

export function useSpotPrice(): SpotPriceState {
  // Cache-first: the card shows a number on the very first paint, before any
  // network call resolves, which is also what keeps a rate-limited reload
  // from flashing an error state.
  const [price, setPrice] = useState<SpotPrice | null>(readCachedPrice);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  const run = useCallback(async (): Promise<boolean> => {
    const res = await fetchSpotPrice();
    if (res.data !== null) setPrice(res.data);
    setError(res.error);
    setStale(res.stale || (res.error !== null && res.data !== null));
    return res.error === null;
  }, []);

  const { refresh } = usePolling(run, env.bitcoin.refreshSeconds, { maxBackoffSeconds: 300 });

  return {
    price,
    status: price !== null ? 'ready' : error !== null ? 'error' : 'loading',
    stale,
    error,
    refresh,
  };
}

/* ------------------------------------------------------------------ */
/* History (chart background series)                                   */
/* ------------------------------------------------------------------ */

export interface HistoryState {
  readonly history: readonly PricePoint[];
  readonly status: SectionStatus;
  readonly error: string | null;
}

/** 6h cadence; `fetchHistory` serves its 12h localStorage cache in between. */
const HISTORY_POLL_SECONDS = 6 * 60 * 60;

export function useBtcHistory(): HistoryState {
  const [history, setHistory] = useState<readonly PricePoint[]>([]);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (): Promise<boolean> => {
    const res = await fetchHistory();
    if (res.data !== null && res.data.length > 0) setHistory(res.data);
    setError(res.error);
    return res.error === null;
  }, []);

  const { failureCount } = usePolling(run, HISTORY_POLL_SECONDS, { maxBackoffSeconds: 900 });

  return {
    history,
    status:
      history.length > 0 ? 'ready' : error !== null || failureCount > 0 ? 'error' : 'loading',
    error,
  };
}

/* ------------------------------------------------------------------ */
/* Fear & Greed                                                        */
/* ------------------------------------------------------------------ */

export interface FearGreedState {
  readonly reading: FearGreed | null;
  readonly status: SectionStatus;
}

/** The index only recomputes daily; 30 min is already generous. */
const FNG_POLL_SECONDS = 30 * 60;

export function useFearGreed(): FearGreedState {
  const [reading, setReading] = useState<FearGreed | null>(null);
  const [failed, setFailed] = useState(false);

  const run = useCallback(async (): Promise<boolean> => {
    const res = await fetchFearGreed();
    if (res.data !== null) setReading(res.data);
    setFailed(res.error !== null && res.data === null);
    return res.error === null;
  }, []);

  usePolling(run, FNG_POLL_SECONDS, { maxBackoffSeconds: 900 });

  return {
    reading,
    status: reading !== null ? 'ready' : failed ? 'error' : 'loading',
  };
}

/* ------------------------------------------------------------------ */
/* Halving                                                             */
/* ------------------------------------------------------------------ */

export interface HalvingState {
  /** Never null — with no network the height is projected from block 840,000. */
  readonly estimate: HalvingEstimate;
  readonly tip: ChainTip | null;
}

const TIP_POLL_SECONDS = 5 * 60;

export function useHalving(): HalvingState {
  const [tip, setTip] = useState<ChainTip | null>(null);

  const run = useCallback(async (): Promise<boolean> => {
    const res = await fetchChainTip();
    if (res.data !== null) setTip(res.data);
    return res.error === null;
  }, []);

  usePolling(run, TIP_POLL_SECONDS, { maxBackoffSeconds: 1800 });

  // Recomputed whenever the tip moves. Deliberately NOT on a 1s timer: the
  // countdown is rendered at day/month granularity, so a ticking clock would
  // re-render the whole card (chart included) for no visible change.
  const estimate = useMemo(
    () => estimateHalving({ height: tip?.height ?? null, blockMs: tip?.blockMs ?? null }),
    [tip],
  );

  return { estimate, tip };
}

/* ------------------------------------------------------------------ */
/* Newhedge (optional pre-calculated power-law values)                 */
/* ------------------------------------------------------------------ */

/**
 * spec.md's optional enhancement. Unset — which is every free-plan user, since
 * Newhedge's free tier has no API access at all — this is inert and the
 * client-side maths in powerLaw.ts stands alone. See DECISIONS.md.
 */
export function useNewhedge(): NewhedgePowerLaw | null {
  const [values, setValues] = useState<NewhedgePowerLaw | null>(null);

  useEffect(() => {
    if (!isConfigured.newhedge) return;
    let cancelled = false;
    void fetchNewhedgePowerLaw(env.bitcoin.newhedgeToken).then((v) => {
      if (!cancelled) setValues(v);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return values;
}

/* ------------------------------------------------------------------ */
/* Wallet stack                                                        */
/* ------------------------------------------------------------------ */

/**
 * Built once at module load, not per render: `usePersistentState` uses the
 * `initial` value both in its lazy initialiser and in its cross-tab re-read,
 * and a fresh array each render would give the wallets new ids on every
 * storage event.
 */
const INITIAL_WALLETS: Wallet[] = defaultWallets();

export interface WalletStackState {
  readonly wallets: readonly Wallet[];
  readonly add: () => void;
  readonly rename: (id: string, name: string) => void;
  readonly setAmount: (id: string, btc: number) => void;
  readonly remove: (id: string) => void;
}

/**
 * `usePersistentState`, NOT `useDailyState` — a stack is not a daily
 * checklist and must survive midnight, refreshes and browser restarts.
 * `isWalletList` guards the read, so a hand-edited or corrupt blob resets to
 * the default instead of white-screening the dashboard.
 */
export function useWalletStack(): WalletStackState {
  const [wallets, setWallets] = usePersistentState<Wallet[]>(
    'btc',
    'wallets',
    INITIAL_WALLETS,
    isWalletList,
  );

  const add = useCallback(() => setWallets((w) => addWallet(w, 'New wallet', 0)), [setWallets]);
  const rename = useCallback(
    (id: string, name: string) => setWallets((w) => updateWallet(w, id, { name })),
    [setWallets],
  );
  const setAmount = useCallback(
    (id: string, btc: number) => setWallets((w) => updateWallet(w, id, { btc })),
    [setWallets],
  );
  const remove = useCallback((id: string) => setWallets((w) => removeWallet(w, id)), [setWallets]);

  return { wallets, add, rename, setAmount, remove };
}
